/** Business timezone for the POS platform (Pakistan Standard Time, UTC+5, no DST). */
export const APP_TIMEZONE = "Asia/Karachi"

const dateOnlyFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

const timeFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: APP_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
})

const dateTimeFmt = new Intl.DateTimeFormat("en-PK", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
})

const longDateFmt = new Intl.DateTimeFormat("en-PK", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "long",
  day: "numeric",
})

const shortDateFmt = new Intl.DateTimeFormat("en-PK", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "short",
  day: "numeric",
})

function asDate(value: string | Date): Date | null {
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Calendar date in PKT as `YYYY-MM-DD` (for `<input type="date">`). */
export function formatDateInAppTz(value: string | Date = new Date()): string {
  const d = asDate(value)
  return d ? dateOnlyFmt.format(d) : ""
}

/** Clock time in PKT as `HH:mm:ss`. */
export function formatTimeInAppTz(value: string | Date): string {
  const d = asDate(value)
  if (!d) return ""
  return timeFmt.format(d).replace(/^24:/, "00:")
}

/** Human-readable PKT datetime for receipts / UI. */
export function formatDateTimeInAppTz(value: string | Date): string {
  const d = asDate(value)
  return d ? dateTimeFmt.format(d) : String(value)
}

/** Long date label in PKT (e.g. invoice themes). */
export function formatLongDateInAppTz(value: string | Date): string {
  const d = asDate(value)
  return d ? longDateFmt.format(d) : String(value)
}

/** Short date label in PKT. */
export function formatShortDateInAppTz(value: string | Date): string {
  const d = asDate(value)
  return d ? shortDateFmt.format(d) : String(value)
}

/** Today's calendar date in PKT. */
export function todayInAppTz(now = new Date()): string {
  return formatDateInAppTz(now)
}

/**
 * Local PKT calendar day start → now, as ISO strings for the invoices API.
 * Prefer this over browser-local midnight so "today's sales" matches PKT.
 */
export function todayRangeIso(now = new Date()) {
  const today = todayInAppTz(now)
  // PKT = UTC+5 → day start is 19:00 UTC previous calendar day
  const from = new Date(`${today}T00:00:00+05:00`)
  return { from: from.toISOString(), to: now.toISOString() }
}
